import React, { useState, useEffect } from 'react';

// Icon set configurations - rotates randomly for fun!
const ICON_SETS = [
  {
    name: 'stars',
    filled: 'fas fa-star text-warning',
    empty: 'far fa-star text-muted',
    label: '⭐'
  },
  {
    name: 'cats',
    filled: 'fas fa-cat text-success',
    empty: 'fas fa-cat text-muted',
    label: '🐱'
  },
  {
    name: 'hearts',
    filled: 'fas fa-heart text-danger',
    empty: 'far fa-heart text-muted',
    label: '❤️'
  },
  {
    name: 'paw',
    filled: 'fas fa-paw text-primary',
    empty: 'fas fa-paw text-muted',
    label: 'paw'
  },
  {
    name: 'fire',
    filled: 'fas fa-fire text-danger',
    empty: 'fas fa-fire text-muted',
    label: '🔥'
  }
];

/**
 * Fun Rotating Star Rating Component - Icons change every 3-5 mins!
 * @param {number} rating - Current rating (0-maxStars)
 * @param {number} maxStars - Maximum stars (default: 5)
 * @param {function} onRate - Callback when rating changes (newRating)
 * @param {boolean} readOnly - Display only, no interaction (default: false)
 * @param {string} size - sm, md, lg (default: md)
 * @param {boolean} showValue - Show numeric value next to stars (default: false)
 * @param {string} className - Additional CSS classes
 */
const StarRating = ({
  rating = 0,
  maxStars = 5,
  onRate = null,
  readOnly = false,
  size = 'md',
  showValue = false,
  className = ''
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [currentIconSet, setCurrentIconSet] = useState(0);

  // Rotate icon sets randomly every 3-5 minutes for fun!
  useEffect(() => {
    const rotateIcons = () => {
      const randomIndex = Math.floor(Math.random() * ICON_SETS.length);
      setCurrentIconSet(randomIndex);

      // Schedule next rotation in 3-5 minutes
      const nextInterval = (3 + Math.random() * 2) * 60 * 1000; // 3-5 mins in ms
      return setTimeout(rotateIcons, nextInterval);
    };

    const timeoutId = rotateIcons();
    return () => clearTimeout(timeoutId);
  }, []);

  const sizeClasses = {
    sm: 'fs-6',
    md: 'fs-5',
    lg: 'fs-4'
  };

  const handleClick = (starIndex) => {
    if (!readOnly && onRate) {
      onRate(starIndex);
    }
  };

  const handleMouseEnter = (starIndex) => {
    if (!readOnly) {
      setHoverRating(starIndex);
    }
  };

  const handleMouseLeave = () => {
    if (!readOnly) {
      setHoverRating(0);
    }
  };

  const getStarClass = (starIndex) => {
    const displayRating = hoverRating || rating;
    const iconSet = ICON_SETS[currentIconSet];
    if (starIndex <= displayRating) {
      return iconSet.filled; // Filled icon
    }
    return iconSet.empty; // Empty icon
  };

  const currentSet = ICON_SETS[currentIconSet];

  return (
    <div className={`d-inline-flex align-items-center ${className}`}>
      <div
        className={`star-rating ${sizeClasses[size]} ${!readOnly ? 'cursor-pointer' : ''}`}
        onMouseLeave={handleMouseLeave}
        style={{ transition: 'all 0.3s ease' }}
      >
        {[...Array(maxStars)].map((_, index) => {
          const starValue = index + 1;
          return (
            <i
              key={starValue}
              className={getStarClass(starValue)}
              onClick={() => handleClick(starValue)}
              onMouseEnter={() => handleMouseEnter(starValue)}
              style={{
                cursor: readOnly ? 'default' : 'pointer',
                marginRight: '2px',
                transition: 'all 0.3s ease',
                opacity: 1
              }}
            />
          );
        })}
      </div>
      {showValue && rating > 0 && (
        <span className="ms-2 text-muted small">
          {currentSet.label} {rating}/{maxStars}
        </span>
      )}
    </div>
  );
};

export default StarRating;
